import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useScrollHighlight } from "@/hooks/use-scroll-highlight";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar as CalendarWidget } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckSquare, Calendar, ChevronRight, AlarmClock, Plus, Loader2, Clock, Link2, PawPrint, MessageSquare, X, MoreVertical, Pencil, Trash2, ChevronDown, ChevronUp, ClipboardList } from "lucide-react";
import { AnimalSearchSelect } from "@/components/tasks/animal-search-select";
import { InteractionSearchSelect } from "@/components/tasks/interaction-search-select";
import { TaskSearchSelect } from "@/components/tasks/task-search-select";
import { TaskAnimalLinker } from "@/components/tasks/task-animal-linker";
import { TaskInteractionLinker } from "@/components/tasks/task-interaction-linker";
import { PriorityBadge } from "@/components/tasks/priority-badge";
import { ExpandCollapseButton } from "@/components/ui/expand-collapse-button";
import { Link } from "wouter";
import type { TaskAnimalLink, TaskInteractionLink } from "@/components/tasks/task-types";

interface TaskLabel {
  labelId: string;
  labelName: string;
  labelColor: string | null;
}

interface MyTask {
  id: string;
  title: string;
  boardId: string;
  boardName: string;
  statusId: string | null;
  statusName: string | null;
  dueDate: string | null;
  priority: string | null;
  source: string | null;
  type: string;
  createdAt: string;
  animalCount: number;
  firstAnimalName: string | null;
  interactionCount: number;
  labels: TaskLabel[];
  checklist?: { done: number; total: number } | null;
}

interface MyReminder {
  id: string;
  title: string;
  description: string | null;
  boardId: string;
  boardName: string;
  statusId: string | null;
  statusName: string | null;
  dueDate: string | null;
  reminderDate: string | null;
  reminderTime: string | null;
  priority: string | null;
  type: string;
  createdAt: string;
  linkedTaskId: string | null;
  animals: TaskAnimalLink[];
  interactions: TaskInteractionLink[];
  checklist?: { done: number; total: number } | null;
  linkedTask?: { id: string; title: string; type: string; boardName: string } | null;
}

interface BoardStatus {
  id: string;
  name: string;
  isArchived: boolean;
  isDefault?: boolean;
  sortOrder: number;
}

type WidgetTab = "tasks" | "reminders";

function formatTime12h(time24: string): string {
  const [h, m] = time24.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function isOverdue(dueDate: string | null): boolean {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dueDate) < today;
}

function isDone(statusName: string | null): boolean {
  if (!statusName) return false;
  const lower = statusName.toLowerCase();
  return lower === "done" || lower === "complete" || lower === "completed";
}

function StatusSelectCompact({ task, onUpdate }: {
  task: { id: string; boardId: string; statusId: string | null; statusName: string | null };
  onUpdate: (taskId: string, statusId: string) => void;
}) {
  const { data: statusesData } = useQuery<{ statuses: BoardStatus[] }>({
    queryKey: ["/api/task-boards", task.boardId, "statuses"],
  });
  const statuses = (statusesData?.statuses ?? []).filter(s => !s.isArchived);

  if (statuses.length > 0) {
    return (
      <Select
        value={task.statusId ?? ""}
        onValueChange={(val) => onUpdate(task.id, val)}
      >
        <SelectTrigger className="h-6 text-[10px] w-full px-2" data-testid={`dash-task-status-select-${task.id}`}>
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          {statuses.map(s => (
            <SelectItem key={s.id} value={s.id} data-testid={`dash-task-status-option-${task.id}-${s.id}`}>
              {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (task.statusName) {
    return <Badge variant="secondary" className="text-[10px]" data-testid={`dash-task-status-${task.id}`}>{task.statusName}</Badge>;
  }
  return <span className="text-[10px] text-muted-foreground">—</span>;
}

type LinkEntityType = "animal" | "interaction" | "task";

function ReminderForm({
  boardId,
  onDone,
  editingReminder,
}: {
  boardId: string;
  onDone: () => void;
  editingReminder?: MyReminder | null;
}) {
  const { toast } = useToast();
  const isEditing = !!editingReminder;

  const [title, setTitle] = useState(editingReminder?.title ?? "");
  const [reminderDate, setReminderDate] = useState<Date | undefined>(
    editingReminder?.reminderDate ? new Date(editingReminder.reminderDate + "T00:00:00") : undefined
  );
  const [reminderTime, setReminderTime] = useState(editingReminder?.reminderTime ?? "");
  const [description, setDescription] = useState(editingReminder?.description ?? "");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedAnimalIds, setSelectedAnimalIds] = useState<string[]>(
    editingReminder?.animals.map(a => a.animalId) ?? []
  );
  const [selectedAnimalNames, setSelectedAnimalNames] = useState<Map<string, string>>(
    new Map(editingReminder?.animals.map(a => [a.animalId, a.animalName ?? a.animalId]) ?? [])
  );
  const [selectedInteractionIds, setSelectedInteractionIds] = useState<string[]>(
    editingReminder?.interactions.map(i => i.interactionId) ?? []
  );
  const [selectedInteractionNames, setSelectedInteractionNames] = useState<Map<string, string>>(
    new Map(editingReminder?.interactions.map(i => [i.interactionId, i.animalName ?? i.interactionId]) ?? [])
  );
  const initialLinkedTaskId = editingReminder?.linkedTaskId ?? null;
  const [linkedTaskId, setLinkedTaskId] = useState<string | null>(initialLinkedTaskId);
  const [linkedTaskLabel, setLinkedTaskLabel] = useState(
    editingReminder?.linkedTask ? `${editingReminder.linkedTask.boardName}: ${editingReminder.linkedTask.title}` : ""
  );
  const [linkedTaskChanged, setLinkedTaskChanged] = useState(false);
  const [activeLinkType, setActiveLinkType] = useState<LinkEntityType | null>(null);
  const animalLinkIdMap = new Map<string, string>(
    editingReminder?.animals.map(a => [a.animalId, a.id]) ?? []
  );
  const interactionLinkIdMap = new Map<string, string>(
    editingReminder?.interactions.map(i => [i.interactionId, i.id]) ?? []
  );

  const linkAnimalMutation = useMutation({
    mutationFn: async (animalId: string) => apiRequest("POST", `/api/tasks/${editingReminder!.id}/animals`, { animalId }),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const unlinkAnimalMutation = useMutation({
    mutationFn: async (linkId: string) => apiRequest("DELETE", `/api/tasks/${editingReminder!.id}/animals/${linkId}`),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const linkInteractionMutation = useMutation({
    mutationFn: async (interactionId: string) => apiRequest("POST", `/api/tasks/${editingReminder!.id}/interactions`, { interactionId }),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const unlinkInteractionMutation = useMutation({
    mutationFn: async (linkId: string) => apiRequest("DELETE", `/api/tasks/${editingReminder!.id}/interactions/${linkId}`),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const hasAnyLinks = selectedAnimalIds.length > 0 || selectedInteractionIds.length > 0 || linkedTaskId !== null;

  interface ReminderPatchBody {
    title: string;
    reminderDate: string | null;
    reminderTime: string | null;
    description: string | null;
    linkedTaskId?: string | null;
  }

  interface ReminderCreateBody {
    boardId: string;
    title: string;
    reminderDate?: string;
    reminderTime?: string;
    description?: string;
    animalIds?: string[];
    interactionIds?: string[];
    linkedTaskId?: string;
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEditing) {
        const body: ReminderPatchBody = {
          title: title.trim(),
          reminderDate: reminderDate ? reminderDate.toISOString().split("T")[0] : null,
          reminderTime: reminderTime || null,
          description: description.trim() || null,
        };
        if (linkedTaskChanged) {
          body.linkedTaskId = linkedTaskId || null;
        }
        return await apiRequest("PATCH", `/api/tasks/${editingReminder!.id}`, body);
      } else {
        const body: ReminderCreateBody = {
          boardId,
          title: title.trim(),
          reminderDate: reminderDate ? reminderDate.toISOString().split("T")[0] : undefined,
          reminderTime: reminderTime || undefined,
          description: description.trim() || undefined,
        };
        if (selectedAnimalIds.length > 0) body.animalIds = selectedAnimalIds;
        if (selectedInteractionIds.length > 0) body.interactionIds = selectedInteractionIds;
        if (linkedTaskId) body.linkedTaskId = linkedTaskId;
        return await apiRequest("POST", "/api/tasks", body);
      }
    },
    onSuccess: () => {
      invalidateTaskQueries();
      queryClient.invalidateQueries({ queryKey: ["/api/tasks/my-reminders"] });
      toast({ title: isEditing ? "Reminder updated" : "Reminder created" });
      onDone();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  return (
    <div className="p-3 border-t space-y-3" data-testid={isEditing ? "edit-reminder-form" : "quick-create-reminder"}>
      <Input
        placeholder="What do you need a reminder for?"
        value={title}
        onChange={e => setTitle(e.target.value)}
        className="text-sm"
        maxLength={500}
        onKeyDown={e => {
          if (e.key === "Enter" && title.trim() && reminderDate) saveMutation.mutate();
        }}
        data-testid="input-quick-reminder-title"
      />
      <Input
        placeholder="Description (optional)"
        value={description}
        onChange={e => setDescription(e.target.value)}
        className="text-sm"
        maxLength={5000}
        data-testid="input-quick-reminder-description"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="button-quick-reminder-date">
              <Calendar className="w-3 h-3 mr-1" />
              {reminderDate ? reminderDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "Date *"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <CalendarWidget
              mode="single"
              selected={reminderDate}
              onSelect={d => { setReminderDate(d); setDatePickerOpen(false); }}
            />
          </PopoverContent>
        </Popover>
        {reminderTime ? (
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-muted-foreground" />
            <Input
              type="time"
              value={reminderTime}
              onChange={e => setReminderTime(e.target.value)}
              className="text-xs h-8 w-[110px]"
              data-testid="input-quick-reminder-time"
            />
            <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => setReminderTime("")} data-testid="button-clear-quick-reminder-time">
              <X className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setReminderTime("12:00")}
            data-testid="button-add-quick-reminder-time"
          >
            <Clock className="w-3 h-3 mr-1" />
            Add time
          </Button>
        )}
      </div>

      {hasAnyLinks && (
        <div className="flex flex-wrap gap-1" data-testid="quick-reminder-linked-items">
          {selectedAnimalIds.map(id => (
            <Badge key={`a-${id}`} variant="secondary" className="text-xs" data-testid={`quick-reminder-badge-animal-${id}`}>
              <PawPrint className="w-3 h-3 mr-1" />
              {selectedAnimalNames.get(id) || id}
              <button className="ml-1" onClick={() => {
                if (isEditing) {
                  const linkId = animalLinkIdMap.get(id);
                  if (linkId) unlinkAnimalMutation.mutate(linkId);
                }
                setSelectedAnimalIds(prev => prev.filter(a => a !== id));
                setSelectedAnimalNames(prev => { const n = new Map(prev); n.delete(id); return n; });
              }}>
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
          {selectedInteractionIds.map(id => (
            <Badge key={`i-${id}`} variant="secondary" className="text-xs" data-testid={`quick-reminder-badge-interaction-${id}`}>
              <Link2 className="w-3 h-3 mr-1" />
              {selectedInteractionNames.get(id) || id.slice(0, 8)}
              <button className="ml-1" onClick={() => {
                if (isEditing) {
                  const linkId = interactionLinkIdMap.get(id);
                  if (linkId) unlinkInteractionMutation.mutate(linkId);
                }
                setSelectedInteractionIds(prev => prev.filter(i => i !== id));
                setSelectedInteractionNames(prev => { const n = new Map(prev); n.delete(id); return n; });
              }}>
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
          {linkedTaskId && (
            <Badge variant="secondary" className="text-xs" data-testid="quick-reminder-badge-linked-task">
              <ClipboardList className="w-3 h-3 mr-1" />
              {linkedTaskLabel}
              <button className="ml-1" onClick={() => { setLinkedTaskId(null); setLinkedTaskLabel(""); setLinkedTaskChanged(true); }}>
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      {activeLinkType === null ? (
        <div className="flex items-center gap-2 flex-wrap" data-testid="quick-reminder-link-picker">
          <span className="text-xs text-muted-foreground">Link to:</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveLinkType("animal")}
            data-testid="button-quick-reminder-link-animal"
          >
            <PawPrint className="w-3 h-3 mr-1" />
            Animal
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveLinkType("interaction")}
            data-testid="button-quick-reminder-link-interaction"
          >
            <Link2 className="w-3 h-3 mr-1" />
            Interaction
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={linkedTaskId !== null}
            onClick={() => setActiveLinkType("task")}
            data-testid="button-quick-reminder-link-task"
          >
            <ClipboardList className="w-3 h-3 mr-1" />
            Task/Meeting
          </Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="quick-reminder-link-active">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">
              {activeLinkType === "animal" ? "Link Animal" : activeLinkType === "interaction" ? "Link Interaction" : "Link Task/Meeting"}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setActiveLinkType(null)}
              data-testid="button-quick-reminder-link-cancel"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
          {activeLinkType === "animal" && (
            <AnimalSearchSelect
              selectedIds={selectedAnimalIds}
              selectedNames={selectedAnimalNames}
              onAdd={(id, name) => {
                if (isEditing) linkAnimalMutation.mutate(id);
                setSelectedAnimalIds(prev => [...prev, id]);
                setSelectedAnimalNames(prev => new Map(prev).set(id, name));
                setActiveLinkType(null);
              }}
              onRemove={(id) => {
                if (isEditing) {
                  const linkId = animalLinkIdMap.get(id);
                  if (linkId) unlinkAnimalMutation.mutate(linkId);
                }
                setSelectedAnimalIds(prev => prev.filter(a => a !== id));
                setSelectedAnimalNames(prev => { const n = new Map(prev); n.delete(id); return n; });
              }}
              showLabel={false}
              testIdPrefix="quick-reminder"
            />
          )}
          {activeLinkType === "interaction" && (
            <InteractionSearchSelect
              selectedIds={selectedInteractionIds}
              selectedNames={selectedInteractionNames}
              onAdd={(id, displayName) => {
                if (isEditing) linkInteractionMutation.mutate(id);
                setSelectedInteractionIds(prev => [...prev, id]);
                setSelectedInteractionNames(prev => new Map(prev).set(id, displayName));
                setActiveLinkType(null);
              }}
              onRemove={(id) => {
                if (isEditing) {
                  const linkId = interactionLinkIdMap.get(id);
                  if (linkId) unlinkInteractionMutation.mutate(linkId);
                }
                setSelectedInteractionIds(prev => prev.filter(i => i !== id));
                setSelectedInteractionNames(prev => { const n = new Map(prev); n.delete(id); return n; });
              }}
              showLabel={false}
              testIdPrefix="quick-reminder"
            />
          )}
          {activeLinkType === "task" && (
            <TaskSearchSelect
              selectedId={linkedTaskId}
              selectedLabel={linkedTaskLabel}
              onSelect={(id, label) => {
                setLinkedTaskId(id);
                setLinkedTaskLabel(label);
                setLinkedTaskChanged(true);
                setActiveLinkType(null);
              }}
              onClear={() => { setLinkedTaskId(null); setLinkedTaskLabel(""); setLinkedTaskChanged(true); }}
              showLabel={false}
              testIdPrefix="quick-reminder"
            />
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        {isEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDone}
            data-testid="button-reminder-edit-cancel"
          >
            Cancel
          </Button>
        )}
        <Button
          size="sm"
          disabled={!title.trim() || !reminderDate || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          data-testid="button-quick-reminder-submit"
        >
          {saveMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : isEditing ? <Pencil className="h-3 w-3 mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
          {isEditing ? "Save" : "Add"}
        </Button>
      </div>
    </div>
  );
}

function ReminderRow({
  reminder,
  done,
  doneStatusId,
  activeStatusId,
  onStatusChange,
  onEdit,
  onDelete,
  expanded,
  onToggleExpand,
  isHighlighted,
}: {
  reminder: MyReminder;
  done: boolean;
  doneStatusId: string | undefined;
  activeStatusId: string | undefined;
  onStatusChange: (taskId: string, statusId: string) => void;
  onEdit: (reminder: MyReminder) => void;
  onDelete: (reminderId: string) => void;
  expanded: boolean;
  onToggleExpand: () => void;
  isHighlighted?: boolean;
}) {
  const [, setLocation] = useLocation();
  const displayDate = reminder.reminderDate || reminder.dueDate;
  const hasDetails = !!(reminder.description || reminder.animals.length > 0 || reminder.interactions.length > 0 || reminder.linkedTask);

  return (
    <>
      <tr
        id={`reminder-${reminder.id}`}
        className={`border-b last:border-b-0 transition-colors ${done ? "opacity-40" : ""} ${isHighlighted ? "ring-2 ring-primary ring-inset bg-primary/5 animate-pulse" : ""}`}
        data-testid={`row-dashboard-reminder-${reminder.id}`}
      >
        <td className="py-2 pl-3 pr-0 align-middle w-[28px]">
          <Checkbox
            checked={done}
            onCheckedChange={(checked) => {
              const targetStatusId = checked ? doneStatusId : activeStatusId;
              if (targetStatusId) onStatusChange(reminder.id, targetStatusId);
            }}
            data-testid={`checkbox-reminder-done-${reminder.id}`}
          />
        </td>
        <td className="py-2 px-2 sm:px-3 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className={`font-medium text-xs ${done ? "line-through text-muted-foreground" : ""}`} data-testid={`text-dashboard-reminder-title-${reminder.id}`}>{reminder.title}</p>
            {(reminder.animals.length > 0 || reminder.interactions.length > 0 || reminder.linkedTask) && (
              <>
                <span className="w-px h-3 bg-border shrink-0" />
                {reminder.animals.length > 0 && <PawPrint className="w-3 h-3 shrink-0 text-muted-foreground" />}
                {reminder.interactions.length > 0 && <MessageSquare className="w-3 h-3 shrink-0 text-muted-foreground" />}
                {reminder.linkedTask && <ClipboardList className="w-3 h-3 shrink-0 text-muted-foreground" />}
              </>
            )}
          </div>
        </td>
        <td className="py-2 px-1 sm:px-3 align-top text-left whitespace-nowrap">
          <div className="flex items-center gap-1">
            {displayDate ? (
              <span
                className={`inline-flex items-center gap-1 text-[10px] ${isOverdue(displayDate) && !done ? "text-destructive font-medium" : "text-muted-foreground"}`}
                data-testid={`text-dashboard-reminder-date-${reminder.id}`}
              >
                <Calendar className="w-3 h-3 shrink-0" />
                {new Date(displayDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {reminder.reminderTime && (
                  <span className="ml-0.5">{formatTime12h(reminder.reminderTime)}</span>
                )}
              </span>
            ) : (
              <span className="text-[10px] text-muted-foreground">—</span>
            )}
          </div>
        </td>
        <td className="py-2 px-1 align-top w-[32px]">
          <div className="flex items-center gap-0.5">
            {hasDetails && (
              <ExpandCollapseButton
                expanded={expanded}
                onClick={onToggleExpand}
                expandLabel="Details"
                collapseLabel="Details"
                testId={`button-expand-reminder-${reminder.id}`}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`button-reminder-actions-${reminder.id}`}>
                  <MoreVertical className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(reminder)} data-testid={`button-reminder-edit-${reminder.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => onDelete(reminder.id)}
                  data-testid={`button-reminder-delete-${reminder.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </td>
      </tr>
      {expanded && hasDetails && (
        <tr data-testid={`details-panel-row-${reminder.id}`}>
          <td colSpan={4} className="px-4 pb-3 pt-2">
            <div className="ml-11 border-l-2 border-muted pl-3 space-y-2">
              {reminder.description && (
                <p className="text-xs text-foreground/80 line-clamp-4" data-testid={`text-dashboard-reminder-desc-${reminder.id}`}>{reminder.description}</p>
              )}
              {reminder.animals.length > 0 && (
                <TaskAnimalLinker
                  taskId={reminder.id}
                  animals={reminder.animals}
                  canEdit={false}
                  animalSearchQuery=""
                  setAnimalSearchQuery={() => {}}
                  animalSearchResults={undefined}
                />
              )}
              {reminder.interactions.length > 0 && (
                <TaskInteractionLinker
                  taskId={reminder.id}
                  interactions={reminder.interactions}
                  canEdit={false}
                  interactionSearchQuery=""
                  setInteractionSearchQuery={() => {}}
                  interactionSearchResults={undefined}
                />
              )}
              {reminder.linkedTask && (
                <button
                  className="flex items-center gap-1 text-[10px] text-muted-foreground/80 hover:text-foreground hover:underline"
                  onClick={() => setLocation(`/tasks/${reminder.linkedTask!.id}`)}
                  data-testid={`text-dashboard-reminder-linked-${reminder.id}`}
                >
                  <ClipboardList className="w-3 h-3 shrink-0" />
                  {reminder.linkedTask.boardName}: {reminder.linkedTask.title}
                </button>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

interface ReminderNavProps {
  reminderId: string | null;
  token: number;
}

export function DashboardTasksWidget({
  reminderNav,
  showTasks = true,
  showReminders = true,
}: {
  reminderNav?: ReminderNavProps | null;
  showTasks?: boolean;
  showReminders?: boolean;
}) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<WidgetTab>(showReminders ? "reminders" : "tasks");
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [editingReminder, setEditingReminder] = useState<MyReminder | null>(null);
  const [expandedReminderId, setExpandedReminderId] = useState<string | null>(null);
  const { isHighlighted: isReminderHighlighted, trigger: triggerReminderHighlight } = useScrollHighlight({ timeout: 2500 });
  const lastNavTokenRef = useRef<number>(0);
  const [pendingHighlightId, setPendingHighlightId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ tasks: MyTask[] }>({
    queryKey: ["/api/tasks/my-tasks"],
  });

  const { data: remindersData, isLoading: remindersLoading } = useQuery<{ reminders: MyReminder[]; boardId: string }>({
    queryKey: ["/api/tasks/my-reminders"],
  });

  const tasks = [...(data?.tasks ?? [])]
    .filter(t => t.type === "regular" && !isDone(t.statusName))
    .slice(0, 8);

  const allReminders = remindersData?.reminders ?? [];
  const activeReminders = allReminders.filter(r => !isDone(r.statusName));
  const doneReminders = allReminders.filter(r => isDone(r.statusName));
  const reminders = [...activeReminders, ...doneReminders].slice(0, 10);
  const reminderBoardId = remindersData?.boardId ?? "";

  useEffect(() => {
    if (!reminderNav || reminderNav.token === lastNavTokenRef.current) return;
    lastNavTokenRef.current = reminderNav.token;
    setActiveTab("reminders");
    if (reminderNav.reminderId) {
      setPendingHighlightId(reminderNav.reminderId);
    }
  }, [reminderNav]);

  useEffect(() => {
    if (!showReminders && activeTab === "reminders") setActiveTab("tasks");
    if (!showTasks && activeTab === "tasks") setActiveTab("reminders");
  }, [showReminders, showTasks, activeTab]);

  useEffect(() => {
    if (!pendingHighlightId || remindersLoading) return;

    const found = allReminders.some(r => r.id === pendingHighlightId);
    if (found) {
      const targetId = pendingHighlightId;
      setPendingHighlightId(null);
      setTimeout(() => {
        triggerReminderHighlight(targetId, `reminder-${targetId}`);
      }, 200);
    }
  }, [pendingHighlightId, remindersLoading, allReminders, triggerReminderHighlight]);

  const { data: reminderStatusesData } = useQuery<{ statuses: BoardStatus[] }>({
    queryKey: ["/api/task-boards", reminderBoardId, "statuses"],
    enabled: !!reminderBoardId,
  });
  const reminderStatuses = (reminderStatusesData?.statuses ?? []).filter(s => !s.isArchived);
  const doneStatusId = reminderStatuses.find(s => s.name.toLowerCase() === "done")?.id;
  const activeStatusId = reminderStatuses.find(s => s.name.toLowerCase() === "active")?.id ?? reminderStatuses.find(s => s.isDefault)?.id;

  const statusMutation = useMutation({
    mutationFn: async ({ taskId, statusId }: { taskId: string; statusId: string }) => {
      return await apiRequest("PATCH", `/api/tasks/${taskId}`, { statusId });
    },
    onSuccess: () => {
      invalidateTaskQueries();
      queryClient.invalidateQueries({ queryKey: ["/api/tasks/my-reminders"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (taskId: string) => {
      return await apiRequest("PATCH", `/api/tasks/${taskId}/archive`);
    },
    onSuccess: () => {
      invalidateTaskQueries();
      queryClient.invalidateQueries({ queryKey: ["/api/tasks/my-reminders"] });
      toast({ title: "Reminder deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function handleDeleteReminder(reminderId: string) {
    if (window.confirm("Are you sure you want to delete this reminder?")) {
      setExpandedReminderId(prev => prev === reminderId ? null : prev);
      archiveMutation.mutate(reminderId);
    }
  }

  const renderTasksTab = () => {
    if (isLoading) {
      return (
        <div className="p-4 space-y-3" data-testid="loading-dashboard-tasks">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      );
    }
    if (tasks.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center p-6 text-center" data-testid="empty-dashboard-tasks">
          <div>
            <CheckSquare className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No tasks assigned to you</p>
            <Link href="/tasks" className="text-xs text-primary mt-2 inline-block" data-testid="link-view-tasks">View tasks</Link>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-zebra" data-testid="table-dashboard-tasks">
            <thead>
              <tr className="border-b text-[10px] text-muted-foreground uppercase tracking-wider">
                <th className="text-left py-2 px-2 sm:px-4 font-medium">Task</th>
                <th className="text-center py-2 px-1 sm:px-4 font-medium w-[90px] sm:w-[110px]">Status</th>
                <th className="text-center py-2 px-4 font-medium w-[80px]">Priority</th>
                <th className="text-center py-2 px-4 font-medium w-[70px]">Checklist</th>
                <th className="text-center py-2 px-1 sm:px-4 font-medium w-[70px] sm:w-[80px]">Due</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(task => {
                const done = isDone(task.statusName);
                return (
                  <tr
                    key={task.id}
                    className={`border-b last:border-b-0 cursor-pointer transition-colors hover:bg-muted/50 ${done ? "opacity-50" : ""}`}
                    data-testid={`row-dashboard-task-${task.id}`}
                  >
                    <td
                      className="py-2 px-2 sm:px-4 min-w-0"
                      onClick={() => setLocation(`/tasks/${task.id}`)}
                    >
                      <p className={`font-medium text-xs truncate max-w-[180px] sm:max-w-[240px] ${done ? "line-through" : ""}`} data-testid={`text-dashboard-task-title-${task.id}`}>{task.title}</p>
                      <span className="text-[10px] text-muted-foreground/60" data-testid={`text-dashboard-task-board-${task.id}`}>{task.boardName}</span>
                    </td>
                    <td className="py-2 px-1 sm:px-4 align-top text-center" onClick={(e) => e.stopPropagation()}>
                      <StatusSelectCompact task={task} onUpdate={(taskId, statusId) => statusMutation.mutate({ taskId, statusId })} />
                    </td>
                    <td
                      className="py-2 px-4 align-top text-center"
                      onClick={() => setLocation(`/tasks/${task.id}`)}
                    >
                      {task.priority ? (
                        <PriorityBadge priority={task.priority} className="text-[9px]" />
                      ) : (
                        <span className="text-[10px] text-muted-foreground">—</span>
                      )}
                    </td>
                    <td
                      className="py-2 px-4 align-top text-center"
                      onClick={() => setLocation(`/tasks/${task.id}`)}
                    >
                      {task.checklist && task.checklist.total > 0 ? (
                        <span className={`text-[10px] font-medium ${task.checklist.done === task.checklist.total ? "text-emerald-600" : "text-muted-foreground"}`} data-testid={`text-dashboard-task-checklist-${task.id}`}>
                          {task.checklist.done} of {task.checklist.total}
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">—</span>
                      )}
                    </td>
                    <td
                      className="py-2 px-1 sm:px-4 align-top text-center"
                      onClick={() => setLocation(`/tasks/${task.id}`)}
                    >
                      {task.dueDate ? (
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] whitespace-nowrap ${isOverdue(task.dueDate) && !done ? "text-destructive font-medium" : "text-muted-foreground"}`}
                          data-testid={`text-dashboard-task-due-${task.id}`}
                        >
                          <Calendar className="w-3 h-3 shrink-0" />
                          {new Date(task.dueDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="p-3 border-t bg-muted/30 mt-auto">
          <Button variant="ghost" className="w-full text-xs text-muted-foreground" asChild>
            <Link href="/tasks" data-testid="link-all-tasks">
              View All Tasks <ChevronRight className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        </div>
      </>
    );
  };

  const renderRemindersTab = () => {
    if (remindersLoading) {
      return (
        <div className="p-4 space-y-3" data-testid="loading-dashboard-reminders">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      );
    }
    if (allReminders.length === 0 && !showQuickCreate) {
      return (
        <div className="flex-1 flex items-center justify-center p-6 text-center" data-testid="empty-dashboard-reminders">
          <div>
            <AlarmClock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No reminders yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Use the <Plus className="w-3 h-3 inline" /> button above to add one
            </p>
          </div>
        </div>
      );
    }
    return (
      <>
        {reminders.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-zebra" data-testid="table-dashboard-reminders">
              <thead>
                <tr className="border-b text-[10px] text-muted-foreground uppercase tracking-wider">
                  <th className="w-[28px]" />
                  <th className="text-left py-2 px-2 sm:px-3 font-medium">Reminder</th>
                  <th className="text-left py-2 px-1 sm:px-3 font-medium w-[100px] sm:w-[130px]">Date</th>
                  <th className="w-[32px]" />
                </tr>
              </thead>
              <tbody>
                {reminders.map(reminder => {
                  const done = isDone(reminder.statusName);
                  if (editingReminder?.id === reminder.id) {
                    return (
                      <tr key={reminder.id}>
                        <td colSpan={4}>
                          <ReminderForm
                            boardId={reminderBoardId}
                            editingReminder={reminder}
                            onDone={() => setEditingReminder(null)}
                          />
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <ReminderRow
                      key={reminder.id}
                      reminder={reminder}
                      done={done}
                      doneStatusId={doneStatusId}
                      activeStatusId={activeStatusId}
                      onStatusChange={(taskId, statusId) => statusMutation.mutate({ taskId, statusId })}
                      onEdit={(r) => { setExpandedReminderId(null); setEditingReminder(r); }}
                      onDelete={handleDeleteReminder}
                      expanded={expandedReminderId === reminder.id}
                      onToggleExpand={() => setExpandedReminderId(prev => prev === reminder.id ? null : reminder.id)}
                      isHighlighted={isReminderHighlighted(reminder.id)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {showQuickCreate && reminderBoardId && (
          <ReminderForm
            boardId={reminderBoardId}
            onDone={() => setShowQuickCreate(false)}
          />
        )}
      </>
    );
  };

  const bothTabsVisible = showTasks && showReminders;

  return (
    <Card className="min-w-0 overflow-hidden h-full flex flex-col" data-testid="card-dashboard-tasks">
      <CardHeader className="pb-0 border-b flex flex-row items-center gap-2 space-y-0 px-4 pt-3">
        {bothTabsVisible ? (
          <>
            <span className="hidden sm:inline text-[11px] uppercase tracking-wider font-semibold text-muted-foreground/60 mr-2">
              My Work
            </span>
            <div className="flex items-center gap-1 -mb-px">
              <button
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "reminders"
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setActiveTab("reminders")}
                data-testid="tab-reminders"
              >
                <AlarmClock className="h-4 w-4" />
                Reminders
              </button>
              <button
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "tasks"
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setActiveTab("tasks")}
                data-testid="tab-tasks"
              >
                <CheckSquare className="h-4 w-4" />
                My Tasks
              </button>
            </div>
          </>
        ) : (
          <span className="flex items-center gap-2 py-2 text-sm font-medium text-foreground">
            {activeTab === "reminders"
              ? <><AlarmClock className="h-4 w-4" /> Reminders</>
              : <><CheckSquare className="h-4 w-4" /> My Tasks</>
            }
          </span>
        )}
        <div className="flex-1" />
        {activeTab === "reminders" && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowQuickCreate(prev => !prev)}
            data-testid="button-add-reminder-header"
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0 flex-1 flex flex-col">
        {activeTab === "tasks" ? renderTasksTab() : renderRemindersTab()}
      </CardContent>
    </Card>
  );
}
